import {
  FETCH_TAGS_LIST,
  FETCH_TAG,
  FETCH_CONTACT_LIST,
  FETCH_CONTACT_DETAILS_TAGS_LIST,
  CREATE_CONTACT,
  UPDATE_TAGS,
  CREATE_TAG,
  UPLOAD_CSV,
  FETCH_CONTACT,
  FETCH_MAIL_PIECES,
  EDIT_CONTACT,
  DELETE_CONTACT,
  DELETE_CONTACTS_BY_TAGS,
  SEND_CSV_UPLOAD_RESULTS,
  CHANGE_DETAILS_ACTIVITY_VIEW,
  VALIDATE_CONTACT,
  RESEND_MAILPIECE,
  RESEND_LETTER,
  FETCH_LETTERS_PIECES
} from "./types";
import { sortSearchPaginateActions } from "@/store/common/actions";
import * as tagsApi from "@/api-client/tag";
import * as contactApi from "@/api-client/contacts";
import { createNewTagsFromLocal } from "@/store/utils";
import { convertDataFromApi } from "@/utils/util";
import { ActivityTrackingLabelsMap } from "@/constants/common";
import pickBy from "lodash/pickBy";

export default {
  async fetchTagsList(
    { commit },
    { search, onSuccess, page = 1, queryString } = {}
  ) {
    commit({
      type: FETCH_TAGS_LIST.request
    });

    try {
      const resp = await tagsApi.fetchTagsList({ search, page, queryString });
      const { items } = resp;
      commit({
        type: FETCH_TAGS_LIST.success,
        payload: items
      });
      onSuccess && onSuccess(items);
    } catch (e) {
      commit({
        type: FETCH_TAGS_LIST.error,
        error: e.message
      });
    }
  },

  async fetchTagById({ commit }, { payload }) {
    commit({
      type: FETCH_TAG.request
    });

    try {
      const template = await tagsApi.fetchTagById(payload.id);
      commit({
        type: FETCH_TAG.success,
        payload: template
      });
    } catch (e) {
      commit({
        type: FETCH_TAG.error,
        error: e.message
      });
    }
  },

  async fetchContactList(
    { commit },
    { page, sort, page_size, search, queryString, onSuccess } = {}
  ) {
    commit({
      type: FETCH_CONTACT_LIST.request
    });

    try {
      const contacts = await contactApi.fetchContactsList({
        sort,
        page,
        page_size,
        search,
        queryString
      });

      commit({
        type: FETCH_CONTACT_LIST.success,
        payload: contacts
      });
      onSuccess && onSuccess(contacts);
    } catch (e) {
      commit({
        type: FETCH_CONTACT_LIST.error,
        error: e.message
      });
    }
  },

  // temporary solution for contacts details page
  async fetchContactDetailsTagsList(
    { commit },
    { page, sort, page_size, search, queryString, onSuccess } = {}
  ) {
    commit({
      type: FETCH_CONTACT_DETAILS_TAGS_LIST.request
    });

    try {
      const { fetchTagsList } = tagsApi;
      const [specificTagsResp, allTagsResp] = await Promise.all([
        fetchTagsList({
          sort,
          page,
          page_size,
          search,
          queryString
        }),
        fetchTagsList({
          sort,
          page,
          page_size,
          search
        })
      ]);

      const allTags = [...specificTagsResp.items, ...allTagsResp.items];

      //delite duplicated tags
      const uniqueTags = Array.from(new Set(allTags.map(tag => tag.id))).map(
        id => {
          return allTags.find(tag => tag.id === id);
        }
      );

      commit({
        type: FETCH_CONTACT_DETAILS_TAGS_LIST.success,
        payload: uniqueTags
      });
      onSuccess && onSuccess(uniqueTags);
    } catch (e) {
      commit({
        type: FETCH_CONTACT_LIST.error,
        error: e.message
      });
    }
  },

  async validateContact({ commit }, contact) {
    commit({
      type: VALIDATE_CONTACT.request
    });

    try {
      const {
        address_state: state,
        address_country: country,
        address_zip: zip_code,
        address_city: city,
        address_unit: unit,
        address_line_1: street,
        id
      } = contact;

      let validationRes = await contactApi.validateContact({
        state,
        country: country || "US",
        zip_code,
        city,
        unit,
        street
      });

      validationRes = {
        ...validationRes,
        details: pickBy(validationRes.details, item => item !== null)
      };

      commit({
        type: VALIDATE_CONTACT.success,
        payload: {
          id,
          data: validationRes
        }
      });
    } catch (e) {
      commit({
        type: VALIDATE_CONTACT.success,
        payload: e.message
      });
    }
  },

  async fetchLettersPiecesContact({ commit }, { id, onSuccess, onError } = {}) {
    commit({
      type: FETCH_LETTERS_PIECES.request
    });

    try {
      const piecesStatus = await contactApi.fetchLettersStatus(id);
      const activities = await contactApi.processActivities(
        piecesStatus.items,
        false
      );

      commit({
        type: FETCH_LETTERS_PIECES.success,
        payload: activities
      });
    } catch (e) {
      commit({
        type: FETCH_CONTACT.error,
        error: e.message
      });

      onError && onError(e.message);
    }
  },

  async fetchMailPieces(
    { commit },
    { id, onError, onSuccess, withCommit = true }
  ) {
    if (withCommit) {
      commit({
        type: FETCH_MAIL_PIECES.request
      });
    }

    try {
      const piecesStatus = await contactApi.fetchMailPieceStatus(id);
      const activities = await contactApi.processActivities(piecesStatus.items);

      if (withCommit) {
        commit({
          type: FETCH_MAIL_PIECES.success,
          payload: activities
        });
      }

      return activities;
    } catch (e) {
      if (withCommit) {
        commit({
          type: FETCH_MAIL_PIECES.error,
          error: e.message
        });
      }

      onError && onError(e.message);
    }
  },

  async fetchContact({ commit, dispatch }, { id, onSuccess, onError }) {
    commit({
      type: FETCH_CONTACT.request
    });

    try {
      const contact = await contactApi.fetchContact(id);

      if (contact.contact_detail) {
        const { verification_status } = contact.contact_detail;

        contact.verification = {
          verification_status,
          details: pickBy(contact.contact_detail, item => item !== null)
        };
      }

      // contact.activities = await dispatch("fetchMailPieces", {
      //   id,
      //   withCommit: false
      // });

      commit({
        type: FETCH_CONTACT.success,
        payload: contact
      });

      const convertedResp = convertDataFromApi("contact", contact);
      onSuccess ? onSuccess(convertedResp) : null;
    } catch (e) {
      commit({
        type: FETCH_CONTACT.error,
        error: e.message
      });

      onError ? onError(e.message) : null;
    }
  },

  async createContact(
    { commit, dispatch, state },
    { data, onSuccess, onError }
  ) {
    commit({
      type: CREATE_CONTACT.request
    });

    try {
      const tagsList = state.tags;
      data.tags = await createNewTagsFromLocal(dispatch, data.tags, tagsList);
      const resp = await contactApi.createContact(data);
      const contact = { ...data, id: resp.contact_id };
      commit({
        type: CREATE_CONTACT.success,
        payload: contact
      });
      onSuccess ? onSuccess(contact, true) : null;
    } catch (e) {
      commit({
        type: CREATE_CONTACT.error,
        error: e.errors
      });

      onError ? onError(e) : null;
    }
  },

  async editContact({ commit, dispatch, state }, { data, onSuccess, onError }) {
    commit({
      type: EDIT_CONTACT.request
    });

    try {
      const { id, ...rest } = data;

      if (data.tags) {
        const tagsList = state.tags;
        data.tags = await createNewTagsFromLocal(dispatch, data.tags, tagsList);
      }

      const contact = await contactApi.editContact(id, rest);
      commit({ type: EDIT_CONTACT.success, payload: contact });
      onSuccess ? onSuccess(contact) : null;
    } catch (e) {
      commit({
        type: EDIT_CONTACT.error,
        error: e.message
      });

      onError ? onError(e.message) : null;
    }
  },

  async deleteContact({ commit }, { id, onSuccess, onError }) {
    commit({
      type: DELETE_CONTACT.request
    });

    try {
      await contactApi.deleteContact(id);

      commit({
        type: DELETE_CONTACT.success,
        payload: id
      });

      onSuccess ? onSuccess() : null;
    } catch (e) {
      commit({
        type: DELETE_CONTACT.error,
        error: e.message
      });
      onError ? onError(e) : null;
    }
  },

  updateTags({ commit }, data) {
    commit({
      type: UPDATE_TAGS,
      payload: data
    });
  },

  async deleteContactsByTags(
    { commit },
    { tagsQueryString, crossedDeletion, onSuccess, onError } = {}
  ) {
    commit({
      type: DELETE_CONTACTS_BY_TAGS.request
    });

    try {
      const deletedContacts = await contactApi.deleteContactsByTags(
        tagsQueryString,
        crossedDeletion
      );
      console.log(deletedContacts);

      commit({
        type: DELETE_CONTACTS_BY_TAGS.success
      });
      onSuccess ? onSuccess(deletedContacts) : null;
      return deletedContacts;
    } catch (e) {
      commit({
        type: DELETE_CONTACTS_BY_TAGS.error,
        error: e.message
      });
      onError ? onError(e) : null;
    }
  },

  async createTag({ commit }, { payload, onSuccess, onError } = {}) {
    commit({
      type: CREATE_TAG.request
    });

    try {
      const tag = await tagsApi.createTag(payload);

      commit({
        type: CREATE_TAG.success,
        payload: tag
      });

      onSuccess ? onSuccess(tag) : null;
      return tag;
    } catch (e) {
      commit({
        type: CREATE_TAG.error,
        error: e.message
      });

      onError ? onError(e.message) : null;
    }
  },

  async resendMailpiece({ commit }, { id, onSuccess, onError }) {
    commit({
      type: RESEND_MAILPIECE.request
    });

    try {
      const mailpiece = await contactApi.resendMailpiece(id);

      commit({
        type: RESEND_MAILPIECE.success,
        payload: mailpiece
      });

      onSuccess ? onSuccess(mailpiece) : null;
    } catch (e) {
      commit({
        type: RESEND_MAILPIECE.error,
        error: e.message
      });

      onError ? onError(e.message) : null;
    }
  },

  async resendLetter({ commit }, { id, onSuccess, onError }) {
    commit({
      type: RESEND_LETTER.request
    });

    try {
      const letter = await contactApi.resendLetter(id);

      commit({
        type: RESEND_LETTER.success,
        payload: letter
      });

      onSuccess ? onSuccess(letter) : null;
    } catch (e) {
      commit({
        type: RESEND_LETTER.error,
        errors: e.message
      });

      onError ? onError(e.message) : null;
    }
  },

  async changeActivityView({ commit }, view) {
    commit({
      type: CHANGE_DETAILS_ACTIVITY_VIEW,
      payload: view
    });
  },

  async uploadCsv({ commit, dispatch, state }, { data, onSuccess, onError }) {
    commit({
      type: UPLOAD_CSV.request
    });

    try {
      const { tags: rawTags, csv, mappings } = data;

      const tags = await createNewTagsFromLocal(dispatch, rawTags, state.tags);
      const uploadData = {
        tags,
        csv,
        mappings
      };

      const contacts = await contactApi.uploadCsv(uploadData);

      commit({
        type: UPLOAD_CSV.success,
        payload: contacts
      });

      onSuccess ? onSuccess() : null;
    } catch (e) {
      commit({
        type: UPLOAD_CSV.error,
        error: e.message
      });
      onError ? onError(e) : null;
    }
  },

  async sendFlatFileUploadResults(
    { commit, state },
    { data, onSuccess, onError }
  ) {
    commit({
      type: SEND_CSV_UPLOAD_RESULTS.request
    });

    try {
      const contacts = await contactApi.sendFlatFileUploads(data);

      commit({
        type: SEND_CSV_UPLOAD_RESULTS.success,
        payload: contacts
      });

      onSuccess ? onSuccess() : null;
    } catch (e) {
      commit({
        type: SEND_CSV_UPLOAD_RESULTS.error,
        error: e.message
      });
      onError ? onError(e) : null;
    }
  },

  // async fetchMailPieces({ commit, dispatch }, { id, onSuccess, onError }) {
  //   commit({
  //     type: FETCH_MAIL_PIECES.request
  //   });
  //
  //   try {
  //     const mailPieces = await contactApi.fetchMailPieces(id);
  //
  //     const resp = await Promise.all(
  //       mailPieces.items.map(async mailPiece => {
  //         if (mailPiece.campaign_id) {
  //           mailPiece.campaign = await dispatch(
  //             "campaigns/fetchCampaign",
  //             { id: mailPiece.campaign_id },
  //             { root: true }
  //           );
  //         } else if (mailPiece.source !== "campaign" && mailPiece.template_id) {
  //           const templateResponse = await dispatch(
  //             "templates/fetchTemplateById",
  //             { id: mailPiece.template_id },
  //             { root: true }
  //           );
  //
  //           if (templateResponse.data) {
  //             mailPiece.template = {
  //               id: null,
  //               name: templateResponse.data.title
  //             };
  //           } else {
  //             mailPiece.template = templateResponse;
  //           }
  //         }
  //
  //         return mailPiece;
  //       })
  //     );
  //
  //     commit({
  //       type: FETCH_MAIL_PIECES.success,
  //       payload: resp
  //     });
  //
  //     onSuccess ? onSuccess() : null;
  //   } catch (e) {
  //     commit({
  //       type: FETCH_MAIL_PIECES.error,
  //       error: e.message
  //     });
  //     onError ? onError(e) : null;
  //   }
  // },

  ...sortSearchPaginateActions
};
